import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Loader from 'components/atoms/loader';
import { ApplicationState } from 'store';
import { BiddingData, CommentDataTypes } from 'store/modules/bidding_store/types';
import {
  AiManagementCardCounts,
  PanelSection,
  PanelStat,
} from 'store/modules/ai_management_store/types';
import { getTradingMessages } from 'store/modules/monitoring_store/actions';
import { ProposalStatusResponse, getProposal } from 'services/actions/ai_management_proposal';
import { PROPOSAL_STATUS_LABEL } from 'services/ai_management_proposal';
import { formatDate, formatDateTimeLong } from 'services/ai_management_dates';
import { MonitoringMessagesTypes } from 'store/modules/monitoring_store/types';
import { PANEL_SECTIONS, PANEL_SHORTCUTS, PANEL_STATS } from 'services/ai_management_card_modal';
import { buildBiddingUpdates, UPDATE_CATEGORY_ICON } from 'services/ai_management_bidding_updates';
import { taskStateOf } from 'services/ai_management_task_state';
import { Container } from './styles';

const NO_DATA = '—';
const LIST_SIZE = 3;

const pluralize = (total: number, singular: string, plural: string, empty: string) => {
  if (total === 0) return empty;
  return `${total} ${total === 1 ? singular : plural}`;
};

interface AiManagementPanelProps {
  bidding: BiddingData | null;
  comments: CommentDataTypes[];
  counts: AiManagementCardCounts;
  loading: boolean;
  error: boolean;
  onNavigate: (target: string | null, itemId?: number | string) => void;
}

const AiManagementPanel: React.FC<AiManagementPanelProps> = ({
  bidding,
  comments,
  counts,
  loading,
  error,
  onNavigate,
}) => {
  const dispatch = useDispatch();
  // Atalho "Proposta": status e data da última alteração, lidos da proposta da licitação.
  const [proposta, setProposta] = useState<{
    status: ProposalStatusResponse;
    atualizadaEm: string | null;
  } | null>(null);
  const proposalBiddingId = bidding ? Number(bidding.bidding_id) || Number(bidding.id) : null;
  useEffect(() => {
    if (!proposalBiddingId) return undefined;
    let vivo = true;
    getProposal(proposalBiddingId)
      .then((res) => {
        if (!vivo || !res?.data || (res.status ?? 200) >= 400) return;
        setProposta({ status: res.data.status, atualizadaEm: res.data.updated_at ?? null });
      })
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [proposalBiddingId]);
  const monitoringMessages = useSelector<ApplicationState, MonitoringMessagesTypes>(
    (state) => state.storage.monitoring.monitoring.monitoring_messages,
  );

  const electronicTradingId = bidding?.has_electronic_trading
    ? bidding.electronic_trading?.id
    : undefined;

  useEffect(() => {
    if (electronicTradingId) dispatch(getTradingMessages(electronicTradingId));
  }, [electronicTradingId]);

  const latestComments = [...comments].reverse().slice(0, LIST_SIZE);

  const updates = useMemo(() => buildBiddingUpdates(bidding), [bidding]);
  const latestUpdates = updates.slice(0, LIST_SIZE);

  const pendingTasks = useMemo(
    () => (bidding?.tasks ?? []).filter((task) => taskStateOf(task).label !== 'Concluída'),
    [bidding],
  );

  const upcomingTasks = useMemo(
    () =>
      [...pendingTasks]
        .sort((a, b) => {
          if (!a.deadline) return 1;
          if (!b.deadline) return -1;
          return new Date(a.deadline).getTime() - new Date(b.deadline).getTime();
        })
        .slice(0, LIST_SIZE),
    [pendingTasks],
  );

  const messages = useMemo(
    () =>
      [...((electronicTradingId && monitoringMessages[electronicTradingId]) || [])].sort(
        (a, b) => Date.parse(b.message_time) - Date.parse(a.message_time),
      ),
    [electronicTradingId, monitoringMessages],
  );
  const latestMessages = messages.slice(0, LIST_SIZE);

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Loader />
      </div>
    );
  }

  if (error || !bidding) {
    return (
      <div className="alert alert-warning m-4" role="alert">
        Não foi possível carregar o painel desta licitação.
      </div>
    );
  }

  const statValue = (key: PanelStat['key']) => {
    switch (key) {
      case 'anotacoes':
        return pluralize(comments.length, 'anotação', 'anotações', 'Sem anotações');
      case 'tarefas':
        return pluralize(pendingTasks.length, 'pendente', 'pendentes', '0 pendentes');
      case 'atualizacoes':
        return pluralize(
          counts.updates ?? 0,
          'nova atualização',
          'novas atualizações',
          'Nenhuma atualização',
        );
      case 'chat':
        return pluralize(messages.length, 'mensagem', 'mensagens', 'Chat não iniciado');
      default:
        return NO_DATA;
    }
  };

  const renderOpenButton = (label: string, target: string, itemId: number | string) => (
    <button
      type="button"
      className="btn btn-secondary btn-sm d-flex align-items-center flex-shrink-0 ml-3"
      aria-label={`Abrir ${label}`}
      onClick={() => onNavigate(target, itemId)}
    >
      <i className="material-icons">chevron_right</i>
    </button>
  );

  const renderSection = (section: PanelSection) => {
    if (section.key === 'anotacoes' && latestComments.length > 0) {
      return latestComments.map((comment) => (
        <div key={comment.id} className="panel-note rounded mb-2">
          <div className="d-flex justify-content-between align-items-baseline mb-1">
            <span className="panel-note-author font-weight-bold text-truncate">
              {comment.contact_client?.nome}
            </span>
            <span className="panel-date text-nowrap ml-2">
              {formatDateTimeLong(comment.created_at)}
            </span>
          </div>
          <div className="panel-note-message text-break">{comment.message}</div>
        </div>
      ));
    }

    if (section.key === 'atualizacoes' && latestUpdates.length > 0) {
      return latestUpdates.map((update) => (
        <div key={update.id} className="panel-item border d-flex align-items-center p-3 mb-2">
          <span className="panel-item-icon rounded d-flex align-items-center justify-content-center flex-shrink-0 mr-3">
            <i className="material-icons">{UPDATE_CATEGORY_ICON[update.category]}</i>
          </span>
          <div className="flex-grow-1 min-width-0">
            <div className="panel-item-title text-truncate">{update.title}</div>
            <div className="text-muted">{formatDateTimeLong(update.created_at)}</div>
          </div>
          {renderOpenButton(update.title, 'atualizacoes', update.id)}
        </div>
      ));
    }

    if (section.key === 'tarefas' && upcomingTasks.length > 0) {
      return upcomingTasks.map((task) => {
        const { label, badge } = taskStateOf(task);
        return (
          <div key={task.id} className="panel-item border d-flex align-items-center p-3 mb-2">
            <div className="flex-grow-1 min-width-0">
              <div className="d-flex align-items-center mb-1">
                <span className={`badge badge-${badge} mr-2`}>{label}</span>
                <span className="panel-item-title text-truncate min-width-0">{task.title}</span>
              </div>
              <div className="d-flex align-items-center flex-wrap text-muted">
                <span className="d-flex align-items-center mr-3">
                  <i className="material-icons mr-1">event</i>
                  {task.deadline ? formatDateTimeLong(task.deadline) : 'Sem prazo'}
                </span>
                {task.responsible && (
                  <span className="d-flex align-items-center mr-3">
                    <i className="material-icons mr-1">person</i>
                    {task.responsible.name}
                  </span>
                )}
                <span className="panel-count border rounded d-flex align-items-center mr-1">
                  <i className="material-icons mr-1">attach_file</i>
                  {task.counts.attachments}
                </span>
                <span className="panel-count border rounded d-flex align-items-center">
                  <i className="material-icons mr-1">chat_bubble</i>
                  {task.counts.comments}
                </span>
              </div>
            </div>
            {renderOpenButton(task.title, 'tarefas', task.id)}
          </div>
        );
      });
    }

    if (section.key === 'chat' && latestMessages.length > 0) {
      return latestMessages.map((message) => (
        <div key={message.id} className="panel-message border p-3 mb-2">
          <div className="d-flex justify-content-between align-items-baseline mb-1">
            <span className="font-weight-bold text-truncate min-width-0">
              {message.message_holder}
            </span>
            <span className="panel-date text-nowrap ml-2">
              {moment(message.message_time).format('DD/MM/YYYY | k:mm')}
            </span>
          </div>
          <div className="text-break">{message.message_highlight || message.message}</div>
        </div>
      ));
    }

    return <div className="text-muted text-center py-4">{section.empty}</div>;
  };

  return (
    <Container>
      <div className="row">
        {PANEL_STATS.map((stat) => (
          <div key={stat.key} className="col-lg-3 col-sm-6 mb-3">
            <div className="panel-box card border shadow-sm h-100 p-3">
              <div className="panel-stat-label d-flex align-items-center mb-2">
                <i className="material-icons text-success mr-2">{stat.icon}</i>
                {stat.label}
              </div>
              <div className="panel-stat-value font-weight-bold">{statValue(stat.key)}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="row">
        {PANEL_SHORTCUTS.map((shortcut) => (
          <div key={shortcut.target} className="col-xl-6 col-12 mb-3">
            <button
              type="button"
              className="panel-box panel-shortcut card border w-100 h-100 d-flex flex-row align-items-center justify-content-between p-3"
              onClick={() => onNavigate(shortcut.target)}
            >
              <span className="panel-shortcut-info d-flex align-items-center">
                <i className="material-icons mr-2">{shortcut.icon}</i>
                <span className="font-weight-bold">{shortcut.label}</span>
                {shortcut.badge && (
                  <span className="badge badge-danger ml-2">{shortcut.badge}</span>
                )}
              </span>
              <span className="text-primary text-nowrap d-flex align-items-center">
                {shortcut.linkLabel}
                <i className="material-icons ml-1">arrow_forward</i>
              </span>
            </button>
          </div>
        ))}
        {proposta && (
          <div className="col-xl-6 col-12 mb-3">
            <button
              type="button"
              className="panel-box panel-shortcut card border w-100 h-100 d-flex flex-row align-items-center justify-content-between p-3"
              onClick={() => onNavigate('elaborar_proposta')}
            >
              <span className="panel-shortcut-info d-flex align-items-center flex-wrap">
                <i className="material-icons mr-2">description</i>
                <span className="font-weight-bold">Proposta</span>
                <span className="badge badge-light border ml-2">
                  {PROPOSAL_STATUS_LABEL[proposta.status] ?? proposta.status}
                </span>
                {proposta.atualizadaEm && proposta.status !== 'nao_iniciada' && (
                  <small className="text-muted ml-2">
                    Atualizada em {formatDate(proposta.atualizadaEm)}
                  </small>
                )}
              </span>
              <span className="text-primary text-nowrap d-flex align-items-center">
                Ver proposta
                <i className="material-icons ml-1">arrow_forward</i>
              </span>
            </button>
          </div>
        )}
      </div>

      <div className="row">
        {PANEL_SECTIONS.map((section) => (
          <div key={section.key} className="col-xl-6 col-12 mb-3">
            <div className="panel-box card border h-100 p-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <span className="panel-title d-flex align-items-center font-weight-bold">
                  <i className="material-icons mr-2">{section.icon}</i>
                  {section.title}
                </span>
                <button
                  type="button"
                  className="btn btn-link text-primary text-nowrap p-0 d-flex align-items-center"
                  onClick={() => onNavigate(section.target)}
                >
                  Ver todas
                  <i className="material-icons ml-1">arrow_forward</i>
                </button>
              </div>

              {renderSection(section)}
            </div>
          </div>
        ))}
      </div>
    </Container>
  );
};

export default AiManagementPanel;
